<template>
    <loader :loader="isLoading" v-if="isLoading" />
    <div class="animated fadeIn" v-else>
        <b-card>
            <b-container fluid>
                <b-row no-gutters>
                    <b-col>
                        <action-plan-progress :action_plan="action_plan" :pdi="pdi" />
                    </b-col>
                    <b-col align-self="center">
                        <b-dropdown
                            id="dropdown-dropright"
                            class="float-right ml-3"
                            variant="link"
                            size="md"
                            dropleft
                            :no-caret="true"
                        >
                            <template slot="button-content">
                                <i class="fa fa-ellipsis-v fa-lg"></i>
                            </template>
                            <b-dropdown-item
                                id="btn-tab-home-pdi-action-plan-edit"
                                @click="openEditActionPlanModal(id)"
                            >
                                {{ $t('form.button.edit') }}
                            </b-dropdown-item>

                            <b-dropdown-item id="btn-tab-home-pdi-action-plan-delete" @click="deleteActionPlan(id)">
                                {{ $t('form.button.delete') }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <button type="button" class="float-right btn btn-primary" @click="openTheModalCheckIn(id)">
                            <i class="far fa-check-circle"></i>
                            {{ $t('checkIn.checkIn') }}
                        </button>
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <b-card>
            <b-container fluid>
                <b-row>
                    <b-col md="4" xl="3">
                        <informations :data="informations" class="mb-2" />
                        <div>
                            <b-row>
                                <b-col>
                                    <p class="mb-1 ml-1 text-muted text-uppercase">
                                        <small>
                                            <i class="icon-note"></i>
                                            {{ $t('form.label.description') }}
                                        </small>
                                    </p>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    <b-list-group>
                                        <b-list-group-item class="d-flex justify-content-between align-items-center">
                                            <b-badge variant="light" class="text-muted text-wrap">
                                                <div class="description-content">
                                                    <div
                                                        v-if="action_plan.description"
                                                        v-html="action_plan.description"
                                                        class="d-inline-block text-break"
                                                    />
                                                    <div v-else>{{ $t('pdis.noDescription') }}</div>
                                                </div>
                                            </b-badge>
                                        </b-list-group-item>
                                    </b-list-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>
                    <b-col md="8" xl="9">
                        <b-tabs>
                            <b-tab :title="$t('checkIn.evidences')" lazy>
                                <files />
                            </b-tab>
                            <b-tab :title="$t('checkIn.updates')" lazy>
                                <feed :actionPlanId="id" />
                            </b-tab>
                        </b-tabs>
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <edit-action-plan-modal :actionPlanId="id" @reloadPdis="update" />
        <router-view @update-parent="update"></router-view>
    </div>
</template>

<script>
import Loader from '@/views/components/loader'
import { getParam as getUrlParam } from '@/lib/url'
import api from '@/services/api'
import ActionPlanProgress from '../components/ActionPlanProgress.vue'
import Informations from '../components/informations/Informations.vue'
import Files from '../components/tab-content/files/Files.vue'
import Feed from '../components/tab-content/feed/Feed.vue'
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import EditActionPlanModal from '../EditActionPlan.vue'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import { success, danger } from '@/lib/notification'
import { EventBuzz } from '@/main'

export default {
    name: 'Pdi',
    components: {
        Loader,
        ActionPlanProgress,
        Informations,
        Files,
        EditActionPlanModal,
        Feed,
    },
    data() {
        return {
            id: parseInt(getUrlParam('actionPlanId', this)),
            isLoading: true,
            action_plan: {},
            pdi: {},
            informations: {},
        }
    },
    mounted() {
        this.getActionPlanFromAPI()
        EventBuzz.$on('update-parent', this.update)
    },
    methods: {
        async getActionPlanFromAPI() {
            this.isLoading = true
            await api
                .get(`/action-plans/${this.id}`)
                .then((resp) => {
                    this.action_plan = resp.data.data

                    this.getPdiFromAPI(this.action_plan)
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        async getPdiFromAPI(action_plan) {
            await api
                .get(`/pdis/${action_plan.pdi_id}`)
                .then((resp) => {
                    this.pdi = resp.data.data.find((pdi) => pdi.id == action_plan.pdi_id)

                    this.informations = {
                        title: this.pdi.title,
                        due_date: action_plan.due_date,
                        budget: action_plan.budget,
                        status: action_plan.status,
                        tags: action_plan.tags,
                    }
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        openTheModalCheckIn(actionPlanId) {
            this.$router.push({
                path: `/v2/action-plan/view/${actionPlanId}/checkin`,
                query: {
                    userId: this.pdi.user.id,
                },
            })
        },
        openEditActionPlanModal(id) {
            this.$bvModal.show(`edit-action-plan-modal${id}`)
        },
        deleteActionPlan(id) {
            Swal({
                ...deleteConfirmationWithLoading,
                preConfirm: () => {
                    return this.deleteActionPlanFromApi(id)
                },
            })
                .then((resp) => {
                    const { value } = resp
                    if (value) {
                        this.notificationActionPlanDeletedSuccessfully()
                        this.$emit('actionPlanUpdated')
                        this.redirectToHome()
                    }
                })
                .catch((e) => {
                    console.log(e)
                    this.notificationError()
                })
        },
        deleteActionPlanFromApi(id) {
            return api.delete(`/action-plans/${id}`).catch((e) => {
                throw new e()
            })
        },
        notificationActionPlanDeletedSuccessfully() {
            success(
                i18n.t('notification.deletedSuccessfully', {
                    item: i18n.t('form.label.objective'),
                })
            )
        },
        notificationError() {
            danger()
        },
        redirectToHome() {
            this.$router.push({
                path: `/v2/profile/${this.$store.state.currentUser.user.id}`,
            })
        },
        update() {
            this.getActionPlanFromAPI()
        },
    },
}
</script>

<style lang="scss" scoped>
.actions {
    color: #666666;
}
::v-deep .description-content {
    text-align: justify;
    margin: 0.65rem;
    * {
        margin: 0;
    }
}
</style>
